import React, { isValidElement, cloneElement, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { tabletBreakpoint, desktopBreakpoint } from 'react-components'
import { useEmbla } from './use-embla'
import { EmblaCarouselDots } from './dots'
import { EmblaCarouselNav } from './nav-buttons'
import type { EmblaCarouselProps, EmblaOptionsType } from './types'

const Viewport = styled.div<{ $viewportPadding?: string }>`
    overflow: hidden;
    width: 100%;
    height: 100%;
    padding: ${({ $viewportPadding }) => $viewportPadding ?? '0.1rem 1rem'};

    @media (min-width: ${tabletBreakpoint.min}px) {
        padding: ${({ $viewportPadding }) => $viewportPadding ?? '0.1rem 1rem'};
    }

    @media (min-width: ${desktopBreakpoint.min}px) {
        padding: ${({ $viewportPadding }) => $viewportPadding ?? '0.1rem 0'};
    }
`

const Container = styled.div`
    display: flex;
    height: 100%;
    touch-action: pan-y pinch-zoom;

    gap: 0.5rem;

    @media (min-width: ${tabletBreakpoint.min}px) {
        gap: 0.75rem;
    }

    @media (min-width: ${desktopBreakpoint.min}px) {
        gap: 1rem;
    }
`

const Slide = styled.div<{ $visibleSlides?: number }>`
    flex: 0 0 auto;
    min-width: 0;
    position: relative;

    ${({ $visibleSlides }) =>
        $visibleSlides
            ? css`
                  width: calc((100% - (0.5rem * (${$visibleSlides} - 1))) / ${$visibleSlides});

                  @media (min-width: ${tabletBreakpoint.min}px) {
                      width: calc((100% - (0.75rem * (${$visibleSlides} - 1))) / ${$visibleSlides});
                  }

                  @media (min-width: ${desktopBreakpoint.min}px) {
                      width: calc((100% - (1rem * (${$visibleSlides} - 1))) / ${$visibleSlides});
                  }
              `
            : css`
                  width: 100%;
              `}
`

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`

export function EmblaCarousel({
    slides,
    loop = false,
    dragEnabled = true,
    visibleSlides,
    slidesToScroll = 1,
    showDots = false,
    dotsVariant = 'dark',
    showNav = false,
    navVariant = 'dark-overlay',
    navPosition,
    onNavButtonClick,
    externalIndex,
    onSlideChange,
    onEmblaInit,
    autoplay,
    lazyLoadNeighbors = false,
    className,
    viewportPadding,
    navButtonBackground,
    navButtonDisabledOpacity,
    navButtonSize,
    navButtonMargin,
    navButtonIconSize,
}: EmblaCarouselProps) {
    const options: EmblaOptionsType = useMemo(
        () => ({
            loop,
            watchDrag: dragEnabled,
            slidesToScroll,
            align: 'start',
        }),
        [loop, dragEnabled, slidesToScroll],
    )

    const { emblaRef, selectedIndex, canScrollPrev, canScrollNext, scrollPrev, scrollNext, scrollTo } = useEmbla({
        options,
        autoplay,
        onSlideChange,
        onEmblaInit,
        externalIndex,
    })

    const renderedSlides = slides.map((slide, index) => {
        const isVisible =
            !lazyLoadNeighbors || index === selectedIndex || index === selectedIndex - 1 || index === selectedIndex + 1

        return (
            <Slide key={index} $visibleSlides={visibleSlides}>
                {lazyLoadNeighbors && !isVisible
                    ? null
                    : isValidElement(slide)
                      ? cloneElement(slide as React.ReactElement<any>)
                      : slide}
            </Slide>
        )
    })

    return (
        <Wrapper className={className}>
            <Viewport ref={emblaRef} $viewportPadding={viewportPadding}>
                <Container>{renderedSlides}</Container>
            </Viewport>

            {showNav && (
                <EmblaCarouselNav
                    canScrollPrev={canScrollPrev}
                    canScrollNext={canScrollNext}
                    onPrev={() => {
                        onNavButtonClick?.()
                        scrollPrev()
                    }}
                    onNext={() => {
                        onNavButtonClick?.()
                        scrollNext()
                    }}
                    variant={navVariant}
                    position={navPosition}
                    buttonBackground={navButtonBackground}
                    buttonDisabledOpacity={navButtonDisabledOpacity}
                    buttonSize={navButtonSize}
                    buttonMargin={navButtonMargin}
                    buttonIconSize={navButtonIconSize}
                />
            )}

            {showDots && (
                <EmblaCarouselDots
                    count={slides.length}
                    selectedIndex={selectedIndex}
                    variant={dotsVariant}
                    onDotClick={scrollTo}
                />
            )}
        </Wrapper>
    )
}

export type { EmblaCarouselProps, EmblaCarouselType } from './types'
